import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useScenarioRunsForScenario } from '@/hooks/useScenarios';
import { REPLAY_RESULT_VARIANT, REPLAY_STATUS_VARIANT } from '@/lib/api/replays.constants';
import { scenarioChannelLabel } from '@/lib/api/scenarios.api';
import { formatDateTime } from '@/utils/format';

import type { ScenarioRun, ScenarioRunCase } from '@/lib/api/scenarios.api';

interface HistoryRow {
  run: ScenarioRun;
  testCase: ScenarioRunCase;
}

const targetLabel = (run: ScenarioRun) => {
  if (run.target_kind === 'live') return 'live';
  if (run.target_kind === 'branch') return 'branch';
  return `commit ${run.commit_id?.slice(0, 8) ?? ''}`.trim();
};

/** Flattens this scenario's cases out of the runs that included it, newest first. */
export const buildHistoryRows = (runs: ScenarioRun[], scenarioId: string): HistoryRow[] =>
  runs
    .flatMap((run) =>
      (run.cases ?? [])
        .filter((c) => c.scenario_id === scenarioId)
        .map((testCase) => ({ run, testCase })),
    )
    .sort((a, b) => b.run.created_at.localeCompare(a.run.created_at));

export const RunHistoryCard = ({ scenarioId }: { scenarioId: string }) => {
  const { data: runs, isLoading } = useScenarioRunsForScenario(scenarioId);
  const rows = buildHistoryRows(runs ?? [], scenarioId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Run history</CardTitle>
        <CardDescription>Every time this scenario ran as part of a suite.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">No runs yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ run, testCase }) => (
                <TableRow key={testCase.id}>
                  <TableCell className="text-xs">
                    <Link
                      to={`/agent-improvement/replays/${testCase.id}`}
                      className="hover:underline"
                    >
                      {formatDateTime(run.created_at)}
                    </Link>
                  </TableCell>
                  <TableCell className="text-xs">{targetLabel(run)}</TableCell>
                  <TableCell className="text-xs">{scenarioChannelLabel(testCase)}</TableCell>
                  <TableCell>
                    <Badge variant={REPLAY_STATUS_VARIANT[testCase.status] ?? 'outline'}>
                      {testCase.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {testCase.result ? (
                      <Badge variant={REPLAY_RESULT_VARIANT[testCase.result]}>
                        {testCase.result}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
